import React, { useState, useEffect, useCallback, useMemo, forwardRef, useRef } from 'react';
import { SpaceBetween, Button, ExpandableSection } from '@cloudscape-design/components';
import { FilterPanel } from './FilterPanel';
import {
  FilterOption,
  RecordAttributes,
  UserViewPreferences,
} from '@amzn/controllershiphub-typescript-client';
import { ControlSummaryPageAGGrid, GridRef } from 'src/components/pages/ControlSummaryPageAGGrid';
import { FilterModel } from 'ag-grid-community';
import { getRollingMonths, getUserPreferences } from 'src/utils/ControlSummaryUtils';
import { DELAY } from 'src/constants';
import {
  formatDisplayNames,
  compareFilters,
  processDistinctValues,
} from 'src/components/Filter/Utils/FilterUtils';
import {
  getControlData,
  getControlDataFirstPage,
  getControlDataRemainingPages,
} from 'src/api/hooks/getControlData';
import { getLazyLoadControlData } from 'src/api/hooks/getLazyLoadControlData';
import { GRC18956_REGIONS, isGrc18956GLData, shouldSkipRegionFilter } from '../../constants';
import {
  getGLControlData,
  getDistinctValuesForFilter,
  updateGLExport,
  createPivotExport,
} from 'src/api/hooks/getGLControlData';

import { useCountryName } from 'src/api/hooks/useCountryName';
import { useLedgerName } from 'src/api/hooks/useLedgerName';
import { useUserAttributes } from 'src/common/hooks/useUserAttributes';
import { useControlMetadata } from 'src/api/hooks/useControlMetadata';
import { clearAllAction } from './Actions/ClearAllAction';
import { applyAction } from './Actions/ApplyAction';
import { UserViewActions } from 'src/components/Filter/Actions/UserViewActions';
import { ExportModal } from 'src/components/exports/ExportModal';
import { PivotTableModal } from 'src/components/modals/PivotTableModal';
import './filterstyles.scss';

interface FilterSectionProps {
  controlId: string;
  onSelectionChange?: (rows: any[]) => void;
  dataType?: 'GL' | 'PO' | 'DEFAULT';
  loadDataByDefault?: boolean;
  additionalDataEnabled?: boolean;
  fallbackControlId?: string;
  onNavigateToExports?: () => void;
  onOpenPOExport?: (activeFilters: Record<string, string[]>) => void;
  onFullyLoaded?: (loaded: boolean, recordCount?: number) => void;
}

const DEFAULT_VIEW = 'Default View' as const;

export const ControlFilterView = forwardRef<GridRef, FilterSectionProps>(
  (
    {
      controlId,
      onSelectionChange,
      dataType = 'DEFAULT',
      loadDataByDefault = true,
      additionalDataEnabled: propAdditionalDataEnabled,
      fallbackControlId,
      onNavigateToExports,
      onOpenPOExport,
      onFullyLoaded,
    },
    ref
  ) => {
    const onFullyLoadedRef = useRef(onFullyLoaded);
    onFullyLoadedRef.current = onFullyLoaded;

    const {
      metadata: metadataData,
      formattedFilters: initialFilters,
      defaultOptions: defaultFilterOptions,
      hasFilters,
      defaultColumnState,
      isLoading: isMetadataLoading,
      error: metadataError,
      setError: setMetadataError,
    } = useControlMetadata(controlId);

    // data states
    const [controlData, setControlData] = useState<RecordAttributes[][]>([]);
    const [dataVersion, setDataVersion] = useState(0);
    const [isLoading, setIsLoading] = useState(loadDataByDefault);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchAfter, setSearchAfter] = useState<string[]>([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Debug loading state
    console.log('=== ControlFilterView Debug ===');
    console.log('loadDataByDefault:', loadDataByDefault);
    console.log('isLoading:', isLoading);
    console.log('dataType:', dataType);
    console.log('controlId:', controlId);
    // filter states
    const [filters, setFilters] = useState<Record<string, string[]>>({});
    const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
    const [distinctValues, setDistinctValues] = useState<Record<string, string[]>>({});
    // ui states
    const [isFullyLoaded, setIsFullyLoaded] = useState(false);
    const [isLoadingFilterViews, setIsLoadingFilterViews] = useState(false);
    const [hasFilterChanges, setHasFilterChanges] = useState(false);
    const [displayNameMapping, setDisplayNameMapping] = useState<Record<string, string>>({});
    const [hasDefaultFilters, setHasDefaultFilters] = useState(false);
    const [selectedView, setSelectedView] = useState<string>(DEFAULT_VIEW);
    const [filterViews, setFilterViews] = useState<Record<string, UserViewPreferences>>({});
    const [agGridFilters, setAgGridFilters] = useState<FilterModel>({});
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [viewInitialFilters, setViewInitialFilters] = useState<FilterModel>({});

    const dataCache = useMemo(() => new Map<string, RecordAttributes[][]>(), []);
    const memoizedControlData = useMemo(() => controlData, [controlData]);
    const [dataFetchStartTime, setDataFetchStartTime] = useState<number>(0);
    const gridRef = useRef<GridRef>(null);
    const activeFiltersRef = useRef(activeFilters);
    const pendingFilterRestoreRef = useRef<(() => void) | null>(null);
    const pendingGridApiRef = useRef<any>(null);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [isPivotExportModalVisible, setIsPivotExportModalVisible] = useState(false);
    const [isPivotModalVisible, setIsPivotModalVisible] = useState(false);

    useEffect(() => {
      activeFiltersRef.current = activeFilters;
    }, [activeFilters]);

    useEffect(() => {
      return () => {
        if (pendingFilterRestoreRef.current && pendingGridApiRef.current) {
          pendingGridApiRef.current.removeEventListener(
            'rowDataUpdated',
            pendingFilterRestoreRef.current
          );
        }
        pendingFilterRestoreRef.current = null;
        pendingGridApiRef.current = null;
      };
    }, []);

    // Use hardcoded regions for GL data instead of useRegion hook
    const { country } = useUserAttributes();
    const regionOptions = isGrc18956GLData(controlId, dataType)
      ? GRC18956_REGIONS.map((region) => ({ label: region, value: region }))
      : [];
    const isRegionLoading = false;

    // Use useCountryName hook for country data based on selected regions (Web MEC pattern)
    const selectedRegions = selectedFilters['Region'] || selectedFilters['region'] || [];
    const { data: countryOptions, isLoading: isCountryLoading } = useCountryName(
      controlId,
      selectedRegions
    );

    // Use useLedgerName hook for ledger data based on selected regions (Web MEC pattern)
    const { data: ledgerOptions, isLoading: isLedgerLoading } = useLedgerName(
      controlId,
      selectedRegions,
      dataType
    );

    // Debug JWT token values
    console.log('=== JWT Token Debug ===');
    console.log('country from JWT:', country);
    console.log('regionOptions:', regionOptions);
    console.log('isRegionLoading:', isRegionLoading);
    console.log('selectedRegions:', selectedRegions);
    console.log('countryOptions:', countryOptions);
    console.log('isCountryLoading:', isCountryLoading);

    // Update distinct values with hardcoded region data
    useEffect(() => {
      console.log('=== Region useEffect triggered ===');
      console.log('regionOptions:', regionOptions);
      if (regionOptions && regionOptions.length > 0) {
        const regionValues = regionOptions.map((option) => option.value);
        console.log('Setting region values:', regionValues);
        setDistinctValues((prev) => {
          const newValues = {
            ...prev,
            region: regionValues,
            Region: regionValues,
          };
          console.log('Updated distinctValues:', newValues);
          return newValues;
        });
      } else {
        console.log('No regionOptions or empty array');
      }
    }, []);

    // Update distinct values with country data from useCountryName hook
    useEffect(() => {
      if (countryOptions && countryOptions.length > 0) {
        const countryValues = countryOptions.map((option) => option.value);
        console.log('Setting country values:', countryValues);
        setDistinctValues((prev) => ({
          ...prev,
          country_name: countryValues,
          'Country Name': countryValues,
        }));
      }
    }, [countryOptions]);

    // Update distinct values with ledger data from useLedgerName hook
    useEffect(() => {
      if (ledgerOptions && ledgerOptions.length > 0) {
        const ledgerValues = ledgerOptions.map((option) => option.value);
        console.log('Setting ledger values:', ledgerValues);
        setDistinctValues((prev) => ({
          ...prev,
          ledger_name: ledgerValues,
          'Ledger Name': ledgerValues,
        }));
      }
    }, [ledgerOptions]);

    useEffect(() => {
      if (ref && gridRef.current) {
        if (typeof ref === 'function') {
          ref(gridRef.current);
        } else {
          ref.current = gridRef.current;
        }
      }
    }, [ref]);

    // Effect to track if any filters are applied
    useEffect(() => {
      setHasDefaultFilters(
        filters && Object.values(filters).some((filterArray) => filterArray.length > 0)
      );
    }, [filters]);

    const cancelledRef = useRef(false);
    const fetchIdRef = useRef(0);

    useEffect(() => {
      cancelledRef.current = false;
      return () => {
        cancelledRef.current = true;
      };
    }, [controlId]);

    const fetchAndApplyControlData = useCallback(
      async (requestFilters: Record<string, string[]>) => {
        const response =
          dataType === 'GL'
            ? await getGLControlData(controlId, requestFilters)
            : dataType === 'PO'
              ? await getLazyLoadControlData(controlId, requestFilters)
              : await getControlData(controlId, requestFilters);

        const { records, totalRecords, distinctValues: newDistinctValues } = response;
        setControlData(records);
        setTotalRecords(totalRecords);
        setSearchAfter(
          dataType === 'GL' || dataType === 'PO' ? (response as any).searchAfter || [] : []
        );
        return { records, totalRecords, newDistinctValues };
      },
      [controlId, dataType]
    );

    const fetchData = useCallback(
      async (currentFilters: Record<string, string[]> = {}) => {
        const startTime = performance.now();
        setDataFetchStartTime(startTime);
        setIsLoading(true);
        setIsFullyLoaded(false);
        const currentFetchId = ++fetchIdRef.current;
        try {
          const hasUserAppliedFilters = Object.entries(currentFilters).some(([key, v]) => {
            if (!Array.isArray(v) || v.length === 0) return false;
            const initial = (initialFilters || {})[key];
            if (!Array.isArray(initial)) return true;
            return JSON.stringify([...v].sort()) !== JSON.stringify([...initial].sort());
          });

          if (dataType === 'DEFAULT') {
            const firstPage = await getControlDataFirstPage(controlId, currentFilters);
            if (cancelledRef.current || fetchIdRef.current !== currentFetchId) return;
            setDataVersion((v) => v + 1);
            setControlData(firstPage.records);
            setTotalRecords(firstPage.totalRecords);
            if (firstPage.distinctValues && !hasUserAppliedFilters) {
              setDistinctValues((prevValues) => ({ ...prevValues, ...firstPage.distinctValues }));
            }
            setIsLoading(false);

            if (firstPage.hasMore && firstPage.lastKey) {
              onFullyLoadedRef.current?.(false, firstPage.records.length);
              getControlDataRemainingPages(controlId, firstPage.enhancedFilters, firstPage.lastKey)
                .then((remainingData) => {
                  if (cancelledRef.current || fetchIdRef.current !== currentFetchId) return;
                  const total = firstPage.totalRecords + remainingData.totalRecords;
                  setControlData((prev) => [...prev, ...remainingData.records]);
                  setTotalRecords(total);
                  setIsFullyLoaded(true);
                  onFullyLoadedRef.current?.(true, total);
                })
                .catch((err) => {
                  console.error('Error loading remaining data:', err);
                  if (!cancelledRef.current && fetchIdRef.current === currentFetchId) {
                    setIsFullyLoaded(true);
                    onFullyLoadedRef.current?.(true, firstPage.totalRecords);
                  }
                });
            } else {
              setIsFullyLoaded(true);
              onFullyLoadedRef.current?.(true, firstPage.totalRecords);
            }
            return;
          }

          // GL and PO paths use the existing fetchAndApplyControlData
          const { newDistinctValues, totalRecords } =
            await fetchAndApplyControlData(currentFilters);
          if (cancelledRef.current || fetchIdRef.current !== currentFetchId) return;

          if (newDistinctValues && !hasUserAppliedFilters) {
            setDistinctValues((prevValues) => ({
              ...prevValues,
              ...newDistinctValues,
            }));
          }
          setIsFullyLoaded(true);
          onFullyLoadedRef.current?.(true, totalRecords);
        } catch (err) {
          console.error('Error fetching data:', err);
          if (!cancelledRef.current && fetchIdRef.current === currentFetchId) {
            setIsFullyLoaded(true);
            onFullyLoadedRef.current?.(true, 0);
          }
        } finally {
          if (!cancelledRef.current && fetchIdRef.current === currentFetchId) {
            setIsLoading(false);
          }
        }
      },
      [controlId, dataType, initialFilters, fetchAndApplyControlData]
    );

    // Refresh data from backend without filters, then apply AG Grid filter model
    // so set filters retain all options while the grid still shows filtered rows.
    const refreshDataPreservingFilters = useCallback(async () => {
      const grid = gridRef.current;
      const savedFilterModel = grid?.api ? grid.api.getFilterModel() : null;

      setIsLoading(true);
      setIsFullyLoaded(false);
      try {
        const baseFilters = initialFilters || {};
        const { newDistinctValues } = await fetchAndApplyControlData(baseFilters);

        if (newDistinctValues) {
          setDistinctValues((prevValues) => ({
            ...prevValues,
            ...newDistinctValues,
          }));
        }

        setIsFullyLoaded(true);

        // Restore filters after data loads using onRowDataUpdated event
        if (grid?.api) {
          if (pendingFilterRestoreRef.current) {
            grid.api.removeEventListener('rowDataUpdated', pendingFilterRestoreRef.current);
          }
          const applyFilters = () => {
            grid.api.removeEventListener('rowDataUpdated', applyFilters);
            pendingFilterRestoreRef.current = null;
            pendingGridApiRef.current = null;

            const filterModel: Record<string, any> = { ...(savedFilterModel || {}) };
            Object.entries(activeFiltersRef.current).forEach(([key, values]) => {
              if (Array.isArray(values) && values.length > 0 && !filterModel[key]) {
                filterModel[key] = {
                  filterType: 'multi',
                  filterModels: [null, { filterType: 'set', values }],
                };
              }
            });
            if (Object.keys(filterModel).length > 0) {
              grid.api.setFilterModel(filterModel);
            }
          };
          pendingFilterRestoreRef.current = applyFilters;
          pendingGridApiRef.current = grid.api;
          grid.api.addEventListener('rowDataUpdated', applyFilters);
        }
      } catch (err) {
        console.error('Error refreshing data:', err);
      } finally {
        setIsLoading(false);
      }
    }, [controlId, dataType, initialFilters, fetchAndApplyControlData]);

    const handleExportData = useCallback(async () => {
      setIsExportModalVisible(true);
    }, []);

    const handleOpenPOExportData = useCallback(async () => {
      onOpenPOExport?.(activeFilters);
    }, [onOpenPOExport, activeFilters]);

    const handleExportSubmit = useCallback(
      async (exportName: string) => {
        try {
          await updateGLExport(controlId, activeFilters, exportName);
          console.log('Export initiated successfully');
        } catch (error) {
          console.error('Export failed:', error);
          throw error;
        }
      },
      [controlId, activeFilters]
    );

    const handlePivotExportSubmit = useCallback(
      async (exportName: string) => {
        try {
          const response = await createPivotExport(controlId, exportName, activeFilters);
          console.log('Pivot export created successfully:', response.exportId);
        } catch (error) {
          console.error('Pivot export failed:', error);
          throw error;
        }
      },
      [controlId, activeFilters]
    );

    const handleOpenPivotExportModal = useCallback(() => {
      setIsPivotExportModalVisible(true);
    }, []);

    const handleLoadMore = useCallback(async () => {
      if (!searchAfter.length || isLoadingMore || (dataType !== 'GL' && dataType !== 'PO')) return;

      setIsLoadingMore(true);
      try {
        const { records, searchAfter: newSearchAfter } =
          dataType === 'GL'
            ? await getGLControlData(controlId, activeFilters, searchAfter)
            : await getLazyLoadControlData(controlId, activeFilters, searchAfter);

        setControlData((prev) => [...prev, ...records]);
        setSearchAfter(newSearchAfter || []);
      } catch (err) {
        console.error('Error loading more data:', err);
      } finally {
        setIsLoadingMore(false);
      }
    }, [controlId, activeFilters, searchAfter, isLoadingMore, dataType]);

    const handleClearFilters = useCallback(async () => {
      try {
        await clearAllAction({
          state: { filters, activeFilters, agGridFilters, distinctValues, defaultFilterOptions },
          actions: { setFilters, setActiveFilters, setAgGridFilters, setDistinctValues, fetchData },
          uiState: { setIsLoading, setHasFilterChanges, setFiltersApplied },
          gridRef,
          dataCache,
        });
        setSelectedFilters({});
        setViewInitialFilters({});
      } catch (err) {
        console.error('Error clearing filters:', err);
      }
    }, [
      filters,
      distinctValues,
      defaultFilterOptions,
      setFilters,
      setActiveFilters,
      setAgGridFilters,
      setDistinctValues,
      fetchData,
      setIsLoading,
      setHasFilterChanges,
      setFiltersApplied,
      gridRef,
      dataCache,
    ]);

    const handleApplyFilters = useCallback(async () => {
      if (!hasFilterChanges) return;
      try {
        const combinedFilters = {
          ...selectedFilters,
          ...Object.entries(agGridFilters).reduce(
            (acc, [key, value]) => {
              if (value?.values) {
                acc[key] = value.values.map(String);
              } else if (value?.filter) {
                acc[key] = [String(value.filter)];
              }
              return acc;
            },
            {} as Record<string, string[]>
          ),
        };

        await applyAction({
          currentFilters: combinedFilters,
          actions: { setFilters, setActiveFilters, setAgGridFilters, setDistinctValues, fetchData },
          uiState: { setIsLoading, setFiltersApplied, setHasFilterChanges },
          gridRef,
        });
        setFilters(selectedFilters);
        setViewInitialFilters({});
      } catch (error) {
        console.error('Failed to clear filters', error);
      }
    }, [
      hasFilterChanges,
      selectedFilters,
      agGridFilters,
      setActiveFilters,
      setAgGridFilters,
      fetchData,
      setIsLoading,
      setFiltersApplied,
      setHasFilterChanges,
      gridRef,
    ]);

    // Check if all required filters are selected for 18956 control
    const areRequiredFiltersSelected = useCallback(() => {
      if (!controlId.startsWith('grc18956')) return true;

      const GL_REQUIRED_FILTERS = [
        'period_name',
        'region',
        'ledger_name',
        'country_name',
        'cost_center',
        'account_number',
      ];

      const PO_REQUIRED_FILTERS = ['accrual_period', 'ledger_name', 'cost_center', 'account'];

      const requiredFilters =
        dataType === 'GL' ? GL_REQUIRED_FILTERS : dataType === 'PO' ? PO_REQUIRED_FILTERS : [];

      return requiredFilters.every((filterName) => selectedFilters[filterName]?.length > 0);
    }, [dataType, controlId, selectedFilters]);

    // effect to monitor filter states
    useEffect(() => {
      const hasActiveFilters =
        Object.values(activeFilters).some((filterArray) => filterArray.length > 0) ||
        Object.keys(agGridFilters).length > 0;

      const hasChanges =
        !compareFilters(selectedFilters, activeFilters) ||
        Object.keys(agGridFilters).length > 0 ||
        Object.keys(selectedFilters).length > 0;

      setHasFilterChanges(hasChanges);
    }, [activeFilters, agGridFilters, selectedFilters]);

    // Initialize filters with metadata
    useEffect(() => {
      const initializeData = async () => {
        if (!isMetadataLoading && initialFilters) {
          let updatedFilters = { ...initialFilters };

          // Auto-populate Period Name for GRC18956 GL data
          if (controlId.startsWith('grc18956') && dataType === 'GL') {
            const today = new Date();
            const dayOfMonth = today.getDate();

            const periods =
              dayOfMonth <= 15 ? ['Prior 2 Month(s)'] : ['Current Month', 'Prior 1 Month(s)'];

            updatedFilters['Period Name'] = periods;
          }

          setFilters(updatedFilters);
          setSelectedFilters(updatedFilters);
          setActiveFilters(updatedFilters);
          setHasFilterChanges(false);

          if (Object.keys(updatedFilters).length > 0 && loadDataByDefault) {
            await fetchData(updatedFilters);
          } else if (!loadDataByDefault) {
            console.log('initializeData: loadDataByDefault is false, setting isLoading to false');
            setIsLoading(false);
            setIsFullyLoaded(true);

            // Load distinct values for filters when not loading data by default
            if (metadataData?.filterOptions && controlId && controlId.startsWith('grc18956')) {
              const filterNames = Object.keys(metadataData.filterOptions).filter(
                (filterName) =>
                  !shouldSkipRegionFilter(filterName, controlId, dataType) && // Skip region - handled by hardcoded regions
                  filterName !== 'Country Name' && // Skip Country Name - handled by useCountryName hook
                  filterName !== 'Ledger Name' // Skip Ledger Name - handled by useLedgerName hook
              );
              const distinctValuesPromises = filterNames.map(async (filterName) => {
                try {
                  const apiFilterName =
                    dataType === 'PO' && filterName === 'account' ? 'account_number' : filterName;

                  if (filterName === 'accrual_period') {
                    const rollingMonths = getRollingMonths();
                    return { [filterName]: rollingMonths };
                  }

                  const values = await getDistinctValuesForFilter(controlId, apiFilterName);
                  return { [filterName]: values };
                } catch (error) {
                  console.error(`Error loading distinct values for ${filterName}:`, error);
                  return { [filterName]: [] };
                }
              });

              Promise.all(distinctValuesPromises).then((results) => {
                const newDistinctValues = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
                setDistinctValues((prev) => ({
                  ...prev,
                  ...newDistinctValues,
                  // Preserve existing region values from hardcoded regions
                }));
              });
            }
          }
        }
      };
      initializeData();
    }, [initialFilters, isMetadataLoading, fetchData, loadDataByDefault]);

    // Apply default column state after initial data load completes
    useEffect(() => {
      if (
        isFullyLoaded &&
        !isLoading &&
        defaultColumnState &&
        defaultColumnState.length > 0 &&
        selectedView === DEFAULT_VIEW
      ) {
        const grid = gridRef.current;
        if (grid && grid.api && grid.api.applyColumnState) {
          // Skip delay in test environment
          setTimeout(() => {
            grid.api.applyColumnState({
              state: defaultColumnState,
              applyOrder: true,
            });
            console.log('[Initial Load] Default column state applied');
          }, DELAY);
        }
      }
    }, [isFullyLoaded, isLoading, defaultColumnState, selectedView]);

    // Effect to update display name mapping when metadata changes
    useEffect(() => {
      if (metadataData?.attributesMetadata) {
        const mapping: Record<string, string> = {};

        metadataData.attributesMetadata.forEach(
          (attr: { name?: string; displayName?: string }): void => {
            if (attr.name) {
              mapping[attr.name] = attr.displayName || attr.name;
            }
          }
        );

        // Ensure specific fields are mapped correctly for control 18956
        if (controlId === 'grc18956') {
          mapping['region'] = 'Region';
          mapping['country_name'] = 'Country Name';
        }

        setDisplayNameMapping(mapping);
      }
    }, [metadataData, controlId]);

    // to get saved views
    useEffect(() => {
      const fetchUserPreferences = async () => {
        setIsLoadingFilterViews(true);
        try {
          const response = await getUserPreferences(controlId);
          if (response.views) {
            setFilterViews(response.views);
          }
        } catch (error) {
          console.error('Error loading filter views:', error);
        } finally {
          setIsLoadingFilterViews(false);
        }
      };
      fetchUserPreferences();
    }, [controlId]);

    const handleFilterChange = useCallback(
      (newFilters: Record<string, string[]>) => {
        const reverseDisplayMapping = Object.entries(displayNameMapping).reduce<
          Record<string, string>
        >((acc, [key, value]) => {
          acc[value] = key;
          return acc;
        }, {});
        const apiFilters = formatDisplayNames(newFilters, reverseDisplayMapping);

        // Update selected filters
        setSelectedFilters(apiFilters);

        // Only update filters when they actually change
        if (!compareFilters(apiFilters, filters)) {
          setFilters(apiFilters);
          setHasFilterChanges(true);
        }
      },
      [displayNameMapping, filters]
    );

    // Applies currently selected filters and updates the grid data
    const handleAgGridFilterChange = useCallback(
      (filters: FilterModel) => {
        try {
          const combinedFilters = {
            ...viewInitialFilters,
            ...filters,
          };
          setAgGridFilters(combinedFilters);

          // Transform grid filters to API format
          const transformedFilters: Record<string, string[]> = {};
          Object.entries(combinedFilters).forEach(
            ([key, value]: [string, { values?: any[]; filter?: any }]): void => {
              if (!value) return;
              if ('values' in value && Array.isArray(value.values)) {
                transformedFilters[key] = value.values.map(String);
              } else if ('filter' in value && value.filter) {
                transformedFilters[key] = [String(value.filter)];
              }
            }
          );

          // Combine with any existing selected filters
          const allFilters = {
            ...selectedFilters,
            ...transformedFilters,
          };

          // Update states without API call
          setActiveFilters(allFilters);
          setHasFilterChanges(true);

          // These states would normally be updated by fetchData
          // We need to set them to maintain test compatibility
          setDataFetchStartTime(performance.now());
          setIsLoading(false);
          setIsFullyLoaded(true);
        } catch (error) {
          console.error('Error applying grid filters:', error);
          // Reset grid filters on error
          if (gridRef.current) {
            gridRef.current.resetFilters();
          }
          setAgGridFilters({});
        }
      },
      [viewInitialFilters, selectedFilters]
    );

    const handleViewSelect = useCallback(
      async (newFilters: Record<string, string[]>, newAgGridFilters: FilterModel) => {
        setIsLoading(true);
        try {
          // Reset grid filters first
          if (gridRef.current) {
            gridRef.current.resetFilters();
          }

          // Transform AG Grid filters
          const transformedAgGridFilters: Record<string, string[]> = {};
          Object.entries(newAgGridFilters).forEach(
            ([key, value]: [string, { values?: any[]; filter?: any }]): void => {
              if (!value) return;
              if ('values' in value && Array.isArray(value.values)) {
                transformedAgGridFilters[key] = value.values.map(String);
              } else if ('filter' in value && value.filter) {
                transformedAgGridFilters[key] = [String(value.filter)];
              }
            }
          );

          // Combine all filters
          const combinedFilters = {
            ...newFilters,
            ...transformedAgGridFilters,
          };

          // Preserve existing distinct values
          setDistinctValues((prevValues) => ({
            ...prevValues,
            ...distinctValues,
          }));

          // Set all states BEFORE fetching data
          setViewInitialFilters(newAgGridFilters);
          setFilters(newFilters);
          setSelectedFilters(newFilters);
          setAgGridFilters(newAgGridFilters);
          setActiveFilters(combinedFilters);

          // Fetch filtered data based on combined filters
          await fetchData(combinedFilters);

          // Apply AG Grid filters after data is loaded
          if (gridRef.current && gridRef.current.api && Object.keys(newAgGridFilters).length > 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            gridRef.current.api.setFilterModel(newAgGridFilters);
          }

          setHasFilterChanges(false);
        } catch (err) {
          console.error('Failed to apply view filters');
        } finally {
          setIsLoading(false);
        }
      },
      [fetchData, distinctValues]
    );

    useEffect(() => {
      return () => {
        setAgGridFilters({});
        setViewInitialFilters({});
        setActiveFilters({});
        setFilters({});
      };
    }, []);

    return (
      <>
        <div className="control-filter-view__container">
          <ExpandableSection
            headerText="Filters"
            defaultExpanded={controlId.startsWith('grc18956')}
            variant="footer"
            disableContentPaddings
          >
            <div className="control-filter-view__filter-panel-container">
              <FilterPanel
                availableFilters={formatDisplayNames(
                  {
                    ...(metadataData?.filterOptions || {}),
                    ...defaultFilterOptions,
                  },
                  displayNameMapping
                )}
                filterValues={processDistinctValues(distinctValues, displayNameMapping)}
                onFiltersUpdate={handleFilterChange}
                currentFilters={formatDisplayNames(selectedFilters, displayNameMapping)}
                isLoading={loadDataByDefault ? isLoading : false}
                controlId={controlId}
                dataType={dataType}
                key={selectedView}
              />
              <div className="control-filter-view__button-container">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    onClick={handleClearFilters}
                    disabled={
                      isLoading ||
                      (!Object.keys(activeFilters).length && !Object.keys(agGridFilters).length)
                    }
                  >
                    Clear all filters
                  </Button>
                  <Button
                    onClick={handleApplyFilters}
                    disabled={isLoading || !hasFilterChanges || !areRequiredFiltersSelected()}
                  >
                    Apply filters
                  </Button>
                </SpaceBetween>
              </div>
            </div>
          </ExpandableSection>
          <UserViewActions
            controlId={controlId}
            filters={filters}
            agGridFilters={agGridFilters}
            displayNameMapping={displayNameMapping}
            onViewSelect={handleViewSelect}
            gridRef={ref}
            onLoadMore={handleLoadMore}
            hasMoreData={searchAfter.length > 0}
            isLoadingMore={isLoadingMore}
            onExportData={
              dataType === 'GL'
                ? handleExportData
                : dataType === 'PO'
                  ? handleOpenPOExportData
                  : undefined
            }
            onPivotExportData={
              dataType === 'GL' && controlId === 'grc18956' ? handleOpenPivotExportModal : undefined
            }
            dataType={dataType}
            setIsPivotModalVisible={setIsPivotModalVisible}
          />
          <div className="control-filter-view__grid-container">
            {controlId != 'grc18956openpo' && (
              <ControlSummaryPageAGGrid
                ref={gridRef}
                controlData={memoizedControlData}
                controlAttributesData={metadataData || null}
                isLoading={isLoading}
                controlId={controlId}
                dataVersion={dataVersion}
                onFilterChange={handleAgGridFilterChange}
                onSelectionChanged={(rows) => {
                  if (onSelectionChange) {
                    onSelectionChange(rows);
                  }
                }}
                dataFetchStartTime={dataFetchStartTime}
                onDataRefresh={refreshDataPreservingFilters}
              />
            )}
          </div>
          <div className="control-filter-view__footer" />
        </div>
        {dataType === 'GL' && (
          <ExportModal
            visible={isExportModalVisible}
            onClose={() => setIsExportModalVisible(false)}
            onExport={handleExportSubmit}
            activeFilters={activeFilters}
            onNavigateToExports={onNavigateToExports}
          />
        )}
        {dataType === 'GL' && controlId === 'grc18956' && (
          <ExportModal
            visible={isPivotExportModalVisible}
            onClose={() => setIsPivotExportModalVisible(false)}
            onExport={handlePivotExportSubmit}
            activeFilters={activeFilters}
            onNavigateToExports={onNavigateToExports}
            title="Export Pivot Data"
            description="This will create a pivot export with your current applied filters and aggregation settings. The pivot export will be available in the Exports section once processing is complete."
          />
        )}
        {dataType === 'GL' && controlId === 'grc18956' && (
          <PivotTableModal
            controlId={controlId}
            filters={activeFilters}
            visible={isPivotModalVisible}
            onDismiss={() => setIsPivotModalVisible(false)}
          />
        )}
      </>
    );
  }
);
